/**
 * Lighthouse Service API client — backend at https://lighthouse-demo.smplify.com.
 *
 * Demo posture: no API gateway. Tenant scope is conveyed via
 * `X-Tenant-Id` header (taken from the existing `apiClient` tenant
 * storage). Bearer is included when present so a future gateway flip
 * is a no-op.
 *
 * All list responses use the documented envelope:
 *   { success, path, data, meta, timestamp, error }
 * `lighthouseGet` returns the unwrapped `data` payload.
 */
import { getMcpAccessToken, getAuthToken, getTenantId } from "./apiClient";

const BASE_URL =
  (import.meta.env.VITE_LIGHTHOUSE_BACKEND_URL as string | undefined) ||
  "https://lighthouse-demo.smplify.com";

export class LighthouseApiError extends Error {
  status: number;
  code: string;
  constructor(message: string, status: number, code = "UNKNOWN") {
    super(message);
    this.name = "LighthouseApiError";
    this.status = status;
    this.code = code;
  }
}

interface LighthouseEnvelope<T> {
  success: boolean;
  path?: string;
  data: T;
  meta?: { pagination?: unknown; sorting?: unknown; message?: string } | null;
  timestamp?: string;
  error?: { code?: string; message?: string; details?: unknown } | null;
}

interface LighthouseRequestOptions {
  params?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

export interface LighthouseResult<T> {
  data: T;
  meta?: LighthouseEnvelope<T>["meta"];
}

export async function lighthouseGet<T>(
  path: string,
  options: LighthouseRequestOptions = {},
): Promise<LighthouseResult<T>> {
  return lighthouseRequest<T>("GET", path, undefined, options);
}

export async function lighthousePost<T>(
  path: string,
  body?: unknown,
  options: LighthouseRequestOptions = {},
): Promise<LighthouseResult<T>> {
  return lighthouseRequest<T>("POST", path, body, options);
}

export async function lighthousePatch<T>(
  path: string,
  body?: unknown,
  options: LighthouseRequestOptions = {},
): Promise<LighthouseResult<T>> {
  return lighthouseRequest<T>("PATCH", path, body, options);
}

async function lighthouseRequest<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body: unknown,
  options: LighthouseRequestOptions,
): Promise<LighthouseResult<T>> {
  let url = `${BASE_URL}${path}`;
  if (options.params) {
    const sp = new URLSearchParams();
    Object.entries(options.params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") sp.append(k, String(v));
    });
    const qs = sp.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  const tenantId = getTenantId();
  if (tenantId) headers["X-Tenant-Id"] = tenantId;
  const token = getMcpAccessToken() || getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      signal: options.signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new Error(
      "Unable to reach the Lighthouse backend. This may be a network issue or a CORS restriction. Check your connection or contact your administrator.",
    );
  }

  if (response.status === 204) return { data: undefined as unknown as T };

  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // non-JSON body
  }

  if (!response.ok) {
    const envelope = parsed as LighthouseEnvelope<unknown> | null;
    throw new LighthouseApiError(
      envelope?.error?.message || `Request failed (${response.status})`,
      response.status,
      envelope?.error?.code || "HTTP_ERROR",
    );
  }

  // Some single-item endpoints may not wrap; tolerate both shapes.
  if (parsed && typeof parsed === "object" && "success" in (parsed as object)) {
    const envelope = parsed as LighthouseEnvelope<T>;
    if (envelope.success === false) {
      throw new LighthouseApiError(
        envelope.error?.message || "Request failed",
        response.status,
        envelope.error?.code || "UNKNOWN",
      );
    }
    return { data: envelope.data, meta: envelope.meta };
  }

  return { data: parsed as T };
}
